import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import * as dropboxService from '../../../../services/dropbox';
import { formatDetailedDate } from '../../utils/dateUtils';

interface MetadataModalProps {
  show: boolean;
  item: any;
  itemType: 'file' | 'folder';
  onClose: () => void;
  onSuccess: () => void;
}

const MetadataModal: React.FC<MetadataModalProps> = ({ 
  show, 
  item, 
  itemType,
  onClose, 
  onSuccess 
}) => {
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState('');
  const [sortOrder, setSortOrder] = useState<string>('0');
  const [loading, setLoading] = useState(false);
  const [allTags, setAllTags] = useState<string[]>([]);

  useEffect(() => {
    if (item) {
      setDescription(item.description || '');
      setTags(item.tags || []);
      setSortOrder(String(item.sort_order || 0));
    }
  }, [item]);

  useEffect(() => {
    if (show && itemType === 'file') {
      loadAllTags();
    }
  }, [show, itemType]);

  const loadAllTags = async () => {
    try {
      const response = await dropboxService.getAllTags();
      if (response?.success && response.data?.tags) {
        setAllTags(response.data.tags);
      }
    } catch (error) {
      // Silently fail - tags are optional
    }
  };

  const handleAddTag = () => {
    const trimmedTag = newTag.trim().toLowerCase();
    if (trimmedTag && !tags.includes(trimmedTag)) {
      setTags([...tags, trimmedTag]);
      setNewTag('');
    }
  };

  const handleRemoveTag = (tag: string) => {
    setTags(tags.filter(t => t !== tag));
  };

  const handleSelectSuggestedTag = (tag: string) => {
    if (!tags.includes(tag)) {
      setTags([...tags, tag]);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      // Update sort order
      const sortOrderValue = sortOrder === '' ? 0 : parseInt(sortOrder, 10);
      if (sortOrderValue !== item.sort_order) {
        if (itemType === 'file') {
          await dropboxService.updateFileSortOrder(item.id, sortOrderValue);
        } else {
          await dropboxService.updateFolderSortOrder(item.id, sortOrderValue);
        }
      }

      // Update tags (only for files)
      if (itemType === 'file') {
        const currentTags = item.tags || [];
        const tagsToAdd = tags.filter((t: string) => !currentTags.includes(t));
        const tagsToRemove = currentTags.filter((t: string) => !tags.includes(t));
        
        if (tagsToAdd.length > 0) {
          await dropboxService.addTags(item.id, tagsToAdd);
        }
        
        for (const tag of tagsToRemove) {
          await dropboxService.removeTag(item.id, tag);
        }
      }

      // Note: Description update would require a new endpoint
      // For now, we'll skip it or you can add it to the updateFile endpoint
      
      toast.success('Metadata updated successfully');
      onSuccess();
      onClose();
    } catch (error) {
      toast.error('Failed to update metadata');
    } finally {
      setLoading(false);
    }
  };

  const suggestedTags = allTags.filter(tag => 
    !tags.includes(tag) && 
    (newTag ? tag.includes(newTag.toLowerCase()) : true)
  ).slice(0, 10);

  return (
    <Modal show={show} onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center">
          <i className={`fas fa-${itemType === 'file' ? 'file' : 'folder'}`} style={{ marginRight: '0.5rem' }}></i>
          Edit Metadata - {item?.name}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          {/* Sort Order - Available for both files and folders */}
          <Form.Group className="mb-4">
            <Form.Label className="fw-semibold mb-3 d-flex align-items-center">
              <i className="fas fa-sort" style={{ marginRight: '0.5rem' }}></i>
              Sort Order
            </Form.Label>
            <Form.Control
              type="number"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              placeholder="0"
              style={{ 
                borderRadius: '6px',
                padding: '0.6rem 0.75rem',
                maxWidth: '200px'
              }}
            />
            <Form.Text className="text-muted" style={{ fontSize: '0.8rem' }}>
              Lower numbers appear first. Use this to manually order items.
            </Form.Text>
          </Form.Group>
          
          {/* Tags - Only for files */}
          {itemType === 'file' && (
            <>
              <Form.Group className="mb-4">
                <Form.Label className="fw-semibold mb-3 d-flex align-items-center">
                  <i className="fas fa-tags" style={{ marginRight: '0.5rem' }}></i>
                  Tags
                </Form.Label>
                <div className="d-flex gap-2 mb-3">
                  <Form.Control
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={(e: React.KeyboardEvent) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddTag();
                      }
                    }}
                    placeholder="Type to add a tag..."
                    style={{ 
                      borderRadius: '6px',
                      padding: '0.6rem 0.75rem'
                    }}
                  />
                  <Button 
                    variant="primary" 
                    onClick={handleAddTag}
                    disabled={!newTag.trim()}
                    style={{ 
                      minWidth: '44px',
                      borderRadius: '6px'
                    }}
                  >
                    <i className="fas fa-plus"></i>
                  </Button>
                </div>

                {/* Current Tags */}
                {tags.length > 0 && (
                  <div className="mb-4">
                    <div className="d-flex flex-wrap" style={{ gap: '0.75rem' }}>
                      {tags.map((tag) => (
                        <span 
                          key={tag} 
                          className="d-inline-flex align-items-center"
                          style={{
                            backgroundColor: '#f8f9fa',
                            border: '1px solid #dee2e6',
                            borderRadius: '6px',
                            padding: '0.5rem 0.75rem',
                            fontSize: '0.875rem',
                            fontWeight: '500',
                            color: '#495057',
                            transition: 'all 0.2s ease',
                            gap: '0.25rem'
                          }}
                        >
                          <i className="fas fa-tag" style={{ fontSize: '0.75rem', color: '#6c757d' }}></i>
                          <span>{tag}</span>
                          <i 
                            className="fas fa-times" 
                            onClick={() => handleRemoveTag(tag)}
                            style={{ 
                              cursor: 'pointer',
                              fontSize: '0.75rem',
                              color: '#6c757d',
                              padding: '0.25rem',
                              marginLeft: '0.25rem'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.color = '#dc3545';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.color = '#6c757d';
                            }}
                          ></i>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Suggested Tags */}
                {suggestedTags.length > 0 && (
                  <div>
                    <small className="text-muted d-block mb-2" style={{ fontSize: '0.8rem', fontWeight: '500' }}>
                      Suggested:
                    </small>
                    <div className="d-flex flex-wrap" style={{ gap: '0.75rem' }}>
                      {suggestedTags.map((tag) => (
                        <span 
                          key={tag} 
                          className="d-inline-flex align-items-center"
                          style={{
                            backgroundColor: '#ffffff',
                            border: '1px dashed #ced4da',
                            borderRadius: '6px',
                            padding: '0.4rem 0.65rem',
                            fontSize: '0.8125rem',
                            color: '#6c757d',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            gap: '0.25rem'
                          }}
                          onClick={() => handleSelectSuggestedTag(tag)}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#f8f9fa';
                            e.currentTarget.style.borderColor = '#0d6efd';
                            e.currentTarget.style.color = '#0d6efd';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#ffffff';
                            e.currentTarget.style.borderColor = '#ced4da';
                            e.currentTarget.style.color = '#6c757d';
                          }}
                        >
                          <i className="fas fa-plus" style={{ fontSize: '0.7rem' }}></i>
                          <span>{tag}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </Form.Group>
            </>
          )}

          {/* File/Folder Info */}
          <div className="border-top pt-4 mt-4">
            <h6 className="text-muted mb-3 fw-semibold d-flex align-items-center" style={{ fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              <i className="fas fa-info-circle" style={{ marginRight: '0.5rem' }}></i>
              {itemType === 'file' ? 'File' : 'Folder'} Information
            </h6>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
              <div style={{ 
                backgroundColor: '#f8f9fa', 
                padding: '0.75rem 1rem', 
                borderRadius: '6px',
                border: '1px solid #e9ecef'
              }}>
                <small className="text-muted d-block mb-1" style={{ fontSize: '0.75rem', fontWeight: '600' }}>CREATED</small>
                <div style={{ fontSize: '0.875rem', color: '#495057' }}>
                  {formatDetailedDate(item?.created_at)}
                </div>
              </div>
              
              <div style={{ 
                backgroundColor: '#f8f9fa', 
                padding: '0.75rem 1rem', 
                borderRadius: '6px',
                border: '1px solid #e9ecef'
              }}>
                <small className="text-muted d-block mb-1" style={{ fontSize: '0.75rem', fontWeight: '600' }}>MODIFIED</small>
                <div style={{ fontSize: '0.875rem', color: '#495057' }}>
                  {formatDetailedDate(item?.updated_at)}
                </div>
              </div>
              
              {itemType === 'file' && item?.size_bytes && (
                <div style={{ 
                  backgroundColor: '#f8f9fa', 
                  padding: '0.75rem 1rem', 
                  borderRadius: '6px',
                  border: '1px solid #e9ecef'
                }}>
                  <small className="text-muted d-block mb-1" style={{ fontSize: '0.75rem', fontWeight: '600' }}>SIZE</small>
                  <div style={{ fontSize: '0.875rem', color: '#495057' }}>
                    {formatFileSize(item.size_bytes)}
                  </div>
                </div>
              )}
              
              {itemType === 'file' && item?.mime_type && (
                <div style={{ 
                  backgroundColor: '#f8f9fa', 
                  padding: '0.75rem 1rem', 
                  borderRadius: '6px',
                  border: '1px solid #e9ecef'
                }}>
                  <small className="text-muted d-block mb-1" style={{ fontSize: '0.75rem', fontWeight: '600' }}>TYPE</small>
                  <div style={{ fontSize: '0.875rem', color: '#495057' }}>
                    {item.mime_type}
                  </div>
                </div>
              )}
            </div>
          </div>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={loading}>
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm" style={{ marginRight: '0.5rem' }} role="status" aria-hidden="true"></span>
              Saving...
            </>
          ) : (
            <>
              <i className="fas fa-save" style={{ marginRight: '0.5rem' }}></i>
              Save Changes
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// Helper function to format file size
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

export default MetadataModal;
